import {
  Location,
  SyncStatus as SyncStatusEnum,
  Task,
  TaskCategory,
  TaskPriority,
  TaskStatus,
} from "@shared/types";
import { ClipboardList, Leaf, MapPin, Save, Send } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import AudioRecorder from "../components/AudioRecorder";
import CameraInput from "../components/CameraInput";
import NotificationBanner from "../components/Notification";
import { db } from "../services/db";
import { syncService } from "../services/sync";

const Submit: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Notification state
  const [notification, setNotification] = useState<{
    message: string;
    type: "success" | "error" | "warning";
    isVisible: boolean;
  }>({
    message: "",
    type: "success",
    isVisible: false,
  });

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [priority, setPriority] = useState<TaskPriority>(TaskPriority.MEDIUM);
  const [category, setCategory] = useState<TaskCategory>(TaskCategory.OTHER);
  const [estimatedDuration, setEstimatedDuration] = useState<number>(30);
  const [photos, setPhotos] = useState<File[]>([]);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [locationError, setLocationError] = useState<string>("");

  // Get current location on mount
  useEffect(() => {
    getCurrentLocation();
  }, []);

  const showNotification = (
    message: string,
    type: "success" | "error" | "warning"
  ) => {
    setNotification({
      message,
      type,
      isVisible: true,
    });
  };

  const hideNotification = () => {
    setNotification((prev) => ({
      ...prev,
      isVisible: false,
    }));
  };

  const getCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            capturedAt: new Date().toISOString(),
          });
          setLocationError("");
        },
        (error) => {
          console.error("Error getting location:", error);
          setLocationError(
            "Unable to get location. Tasks can still be submitted."
          );
        }
      );
    } else {
      setLocationError("Geolocation is not supported by your browser.");
    }
  };

  const validateForm = (): boolean => {
    if (!title.trim()) {
      showNotification("Please enter a task title", "error");
      return false;
    }
    if (title.length < 3 || title.length > 200) {
      showNotification("Title must be between 3 and 200 characters", "error");
      return false;
    }
    if (description.length > 1000) {
      showNotification(
        "Description must be less than 1000 characters",
        "error"
      );
      return false;
    }

    return true;
  };

  const createTask = (status: TaskStatus): Task => {
    const taskId = uuidv4();
    const now = new Date().toISOString();

    return {
      id: taskId,
      localId: taskId,
      userId: localStorage.getItem("userId") || "unknown",
      createdAt: now,
      updatedAt: now,
      submittedAt: status === TaskStatus.SUBMITTED ? now : undefined,

      title,
      description: description || undefined,
      notes: notes || undefined,
      location: location || undefined,

      status,
      priority,
      category,
      estimatedDuration,

      syncStatus: SyncStatusEnum.PENDING,
      retryCount: 0,
    };
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    setIsSavingDraft(true);
    try {
      const task = createTask(TaskStatus.DRAFT);

      // Save task to IndexedDB
      await db.saveTask(task);

      // Save media files if any
      if (photos.length > 0) {
        await db.savePhotos(task.id, photos);
      }
      if (audioFile) {
        await db.saveAudioRecording(task.id, audioFile);
      }

      showNotification("Draft saved successfully!", "success");
      // Navigate after a short delay to let user see the notification
      setTimeout(() => navigate("/"), 1500);
    } catch (error) {
      console.error("Error saving draft:", error);
      showNotification("Failed to save draft. Please try again.", "error");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const task = createTask(TaskStatus.SUBMITTED);

      // Save task to IndexedDB
      await db.saveTask(task);

      // Save media files if any
      if (photos.length > 0) {
        await db.savePhotos(task.id, photos);
      }
      if (audioFile) {
        await db.saveAudioRecording(task.id, audioFile);
      }

      // Queue for sync
      await syncService.queueTask(task);

      // Try to sync immediately if online
      if (navigator.onLine) {
        syncService.syncNow().catch(console.error);
      }

      showNotification("Task submitted successfully!", "success");
      // Navigate after a short delay to let user see the notification
      setTimeout(() => navigate("/"), 1500);
    } catch (error) {
      console.error("Error submitting task:", error);
      showNotification(
        "Failed to submit task. It has been saved locally and will sync when online.",
        "warning"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <NotificationBanner
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
      <div className="min-h-screen bg-earth-50">
        <div className="container mx-auto px-4 py-6 max-w-2xl">
          {/* Header with Navigation */}
          <div className="flex justify-between items-center mb-6 animate-fade-in">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-field rounded-full flex items-center justify-center shadow-soft">
                <Leaf className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="font-display text-2xl text-earth-900">New Task</h1>
                <p className="text-sm text-earth-600">Waddington Park</p>
              </div>
            </div>
            <a
              href="/tasks"
              className="btn-secondary flex items-center gap-2"
            >
              <ClipboardList className="h-4 w-4" />
              <span className="hidden sm:inline">View Tasks</span>
            </a>
          </div>

          <form
            onSubmit={handleSubmit}
            className="card p-6 space-y-6 animate-slide-up"
          >
            {/* Title */}
            <div>
              <label htmlFor="title" className="label label-required">
                Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input"
                placeholder="Brief description of the task"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="label">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="textarea"
                placeholder="Detailed description of the task"
              />
            </div>

            {/* Priority and Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="priority" className="label label-required">
                  Priority
                </label>
                <select
                  id="priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TaskPriority)}
                  className="select"
                  required
                >
                  <option value={TaskPriority.LOW}>Low</option>
                  <option value={TaskPriority.MEDIUM}>Medium</option>
                  <option value={TaskPriority.HIGH}>High</option>
                  <option value={TaskPriority.URGENT}>Urgent</option>
                </select>
              </div>

              <div>
                <label htmlFor="category" className="label label-required">
                  Category
                </label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as TaskCategory)}
                  className="select"
                  required
                >
                  <option value={TaskCategory.MAINTENANCE}>Maintenance</option>
                  <option value={TaskCategory.INSPECTION}>Inspection</option>
                  <option value={TaskCategory.HARVEST}>Harvest</option>
                  <option value={TaskCategory.PLANTING}>Planting</option>
                  <option value={TaskCategory.IRRIGATION}>Irrigation</option>
                  <option value={TaskCategory.PEST_CONTROL}>Pest Control</option>
                  <option value={TaskCategory.EQUIPMENT}>Equipment</option>
                  <option value={TaskCategory.SAFETY}>Safety</option>
                  <option value={TaskCategory.OTHER}>Other</option>
                </select>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="label flex items-center gap-2">
                <MapPin className="h-4 w-4 text-earth-600" />
                Location
              </label>
              {location ? (
                <div className="bg-field-50 border border-field-200 rounded-md p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-field rounded-full"></div>
                    <p className="text-sm text-field-700 font-medium">
                      Location captured
                    </p>
                  </div>
                  <p className="text-sm text-earth-600 mt-1 font-mono">
                    {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                    {location.accuracy && (
                      <span className="text-earth-500">
                        {" "}({Math.round(location.accuracy)}m)
                      </span>
                    )}
                  </p>
                </div>
              ) : locationError ? (
                <div className="bg-harvest-50 border border-harvest-200 rounded-md p-4">
                  <p className="text-sm text-harvest-700">{locationError}</p>
                  <button
                    type="button"
                    onClick={getCurrentLocation}
                    className="mt-2 text-sm font-medium text-harvest-600 hover:text-harvest-700 underline"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <div className="bg-earth-100 border border-earth-200 rounded-md p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-earth-400 rounded-full animate-pulse"></div>
                    <p className="text-sm text-earth-600">Getting location...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Photos */}
            <CameraInput onPhotosChange={setPhotos} maxPhotos={5} />

            {/* Audio Recording */}
            <AudioRecorder onAudioChange={setAudioFile} />

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4 border-t border-earth-200">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isSubmitting}
                className="flex-1 btn-secondary flex items-center justify-center gap-2"
              >
                <Save className="h-4 w-4" />
                {isSavingDraft ? "Saving..." : "Save Draft"}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isSavingDraft}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <Send className="h-4 w-4" />
                {isSubmitting ? "Submitting..." : "Submit Task"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Submit;
